import { observer } from 'mobx-react-lite';
import { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  KpiDropdownActions,
  KpiType,
  StorePerformanceInterval,
  createStorePerformanceRequest,
} from '../../../../../constants';
import { RfidSalesRetaggingResponse, useAsyncAction, useRfid, useUser } from '../../../../../hooks';
import { Icons } from '../../../../../stories/atoms';
import { ColumnFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { Animation, Animations } from '../../../../../stories/molecules';
import { homeTranslations, kpiTargetTranslations } from '../../../../../translations';
import { getKpiTargetValue, isFailureResponse } from '../../../../../utils';
import { WidgetCardContent } from '../../../common/widgetCardContent/WidgetCardContent';
import { MetricItem } from './MetricItem';

export type RfidSalesRetaggingWidgetProps = {
  interval: StorePerformanceInterval;
  currentDate: Date;
  hasPermissionToEditKpi: boolean;
  isToday: boolean;
  handleDropdownDetails: (kpiType: KpiType, action: KpiDropdownActions) => void;
};

export const RfidSalesRetaggingWidget: FC<RfidSalesRetaggingWidgetProps> = observer(
  ({ interval, currentDate }) => {
    const { t } = useTranslation();
    const { rfidSalesRetagging: rfidSalesRetaggingTranslations } = homeTranslations;

    const { fetchRfidSalesRetaggingData } = useRfid();
    // const { fetchForecastForKpis } = useStores();

    const {
      get: { kpiTargetsProps },
    } = useUser();

    const target = getKpiTargetValue(kpiTargetsProps, 'dailyRFIDAddedSales');
    // const isTargetLocked = getKpiTargetProps(kpiTargetsProps, 'dailyRFIDAddedSales')?.isLocked ?? false;

    const {
      result: rfidSalesRetagging,
      isLoading: rfidSalesRetaggingLoading,
      isError: rfidSalesRetaggingError,
      triggerAction: triggerFetchRfidSalesRetagging,
    } = useAsyncAction<RfidSalesRetaggingResponse>();

    // const {
    //   result: salesRetaggingForecast,
    //   isLoading: salesRetaggingForecastLoading,
    //   isError: salesRetaggingForecastError,
    //   triggerAction: triggerFetchSalesRetaggingForecast,
    // } = useAsyncAction<SalesRetaggingPerformanceItem[]>();

    const fetchRfidSalesRetagging = useCallback(async () => {
      const request = createStorePerformanceRequest({
        interval,
        currentDate,
      });

      const response = await fetchRfidSalesRetaggingData(request);

      if (isFailureResponse(response)) {
        throw new Error();
      }

      return response.data;
    }, [interval, currentDate]);

    // const fetchForecastData = useCallback(async () => {
    //   const response = await fetchForecastForKpis<SalesRetaggingPerformanceItem>('rfidAddedSales');

    //   if (isFailureResponse(response)) {
    //     throw new Error();
    //   }

    //   return response.data;
    // }, [currentDate]);

    const fetchWidgetData = () => {
      triggerFetchRfidSalesRetagging(fetchRfidSalesRetagging);
      // if (interval === StorePerformanceInterval.DAILY) {
      //   triggerFetchSalesRetaggingForecast(fetchForecastData);
      // }
    };

    useEffect(() => {
      fetchWidgetData();
    }, [currentDate]);

    // const forecastSalesRetagging = salesRetaggingForecast?.reduce((acc, item) => acc + item.amount, 0) ?? 0;

    // const forecastRetaggingValue = forecastSalesRetagging + (rfidSalesRetagging?.addedSales ?? 0);

    // const targetProgressPercent = target && target != 0 ? (forecastRetaggingValue / target) * 100 : 0;

    const isNoData = rfidSalesRetagging?.addedSales === 0 && rfidSalesRetagging?.totalSuccessfulRetagging === 0;
    // const isPreviousDay = isMultiDayInterval(interval) || !isToday;
    // const isEditKPIDisabled = rfidSalesRetaggingError || !hasPermissionToEditKpi;

    // const getTooltipText = () => {
    //   if (!hasPermissionToEditKpi) {
    //     return t(
    //       kpiTargetsTranslations.noPermissionTooltip.key,
    //       kpiTargetsTranslations.noPermissionTooltip.defaultValue,
    //     );
    //   } else if (isTargetLocked) {
    //     return t(
    //       kpiTargetsTranslations.lockedTargetTooltip.key,
    //       kpiTargetsTranslations.lockedTargetTooltip.defaultValue,
    //     );
    //   } else if (isPreviousDay) {
    //     return t(
    //       kpiTargetsTranslations.previousDayTooltip.key,
    //       kpiTargetsTranslations.previousDayTooltip.defaultValue,
    //     );
    //   } else {
    //     return t(kpiTargetsTranslations.disabledTooltip.key, kpiTargetsTranslations.disabledTooltip.defaultValue);
    //   }
    // };

    return (
      <WidgetCardContent
        headerIcon={Icons.PROFILE}
        headerText={t(rfidSalesRetaggingTranslations.title.key, rfidSalesRetaggingTranslations.title.defaultValue)}
        subHeaderText={t(
          rfidSalesRetaggingTranslations.subTitle.key,
          rfidSalesRetaggingTranslations.subTitle.defaultValue,
        )}
        toolTipText={t(
          rfidSalesRetaggingTranslations.tooltip.key,
          rfidSalesRetaggingTranslations.tooltip.defaultValue,
        )}
        isLoading={rfidSalesRetaggingLoading}
        isError={rfidSalesRetaggingError}
        errorText={t(rfidSalesRetaggingTranslations.error.key, rfidSalesRetaggingTranslations.error.defaultValue)}
        errorRetryHandler={fetchWidgetData}
        lastUpdatedDate={rfidSalesRetagging?.lastUpdated}
        target={
          target
            ? target.toFixed(2)
            : t(kpiTargetTranslations.notSet.key, kpiTargetTranslations.notSet.defaultValue)
        }
        isNoData={isNoData}
        noDataContent={
          <ColumnFlexContainer data-testid="no-data-target" alignItems="center">
            <Animation
              size="small"
              titleType="h3"
              title={`${t(
                kpiTargetTranslations.kpiProperties.target.title.key,
                kpiTargetTranslations.kpiProperties.target.title.defaultValue,
              )}: ${
                target
                  ? `${target.toFixed(2)}`
                  : t(kpiTargetTranslations.notSet.key, kpiTargetTranslations.notSet.defaultValue)
              }`}
              subtitle={t(
                rfidSalesRetaggingTranslations.noData.key,
                rfidSalesRetaggingTranslations.noData.defaultValue,
              )}
              animation={Animations.COOL}
            />
          </ColumnFlexContainer>
        }
        // headerSideContent={
        //   <DropdownMenu
        //     data-testid="kpiDropdown"
        //     dropdownLabel={''}
        //     buttonContentProps={{
        //       iconOptions: { customIcon: <Icon icon={Icons.MORE} /> },
        //     }}
        //     menuItems={[
        //       {
        //         key: target ? 'editkpi' : 'addkpi',
        //         disabled: isEditKPIDisabled || isTargetLocked || isPreviousDay,
        //         hasTooltip: isEditKPIDisabled || isTargetLocked || isPreviousDay,
        //         tooltipText: (
        //           <ContainerV2 padding={[1]}>
        //             <Typography color="white">{getTooltipText()}</Typography>
        //           </ContainerV2>
        //         ),
        //         item: (
        //           <Typography margin={[0, 2, 0]}>
        //             {target
        //               ? t(kpiTargetsTranslations.editKpi.key, kpiTargetsTranslations.editKpi.defaultValue)
        //               : t(kpiTargetsTranslations.addKpi.key, kpiTargetsTranslations.addKpi.defaultValue)}
        //           </Typography>
        //         ),
        //       },
        //     ]}
        //     minWidth={'0px'}
        //     isCompact
        //     initialSelected={[]}
        //     onSelect={(selectedItem) => {
        //       handleDropdownDetails('dailyRFIDAddedSales', selectedItem[0] as KpiDropdownActions);
        //     }}
        //     isOnlyIcon
        //     allowTooltip
        //   />
        // }
        content={
          <ColumnFlexContainer
            flex={1}
            minHeight={[0]}
            padding={[2, 4, 4]}
            data-testid="rfid-sales-retagging-wrapper"
          >
            {rfidSalesRetagging && (
              <ColumnFlexContainer data-testid="chart" flex={1} gap={[2]}>
                {/* {target !== null && (
                  <RowFlexContainer gap={[2]}>
                    {!isPreviousDay && (
                      <WidgetMetricTile
                        metricProperties={{
                          type: WidgetMetricType.ForecastMetric,
                          properties: {
                            metricValue: `${Math.trunc(Math.round(forecastRetaggingValue))}`,
                            forecastIndicator: getDefaultStatus(targetProgressPercent),
                          },
                        }}
                      />
                    )}
                    <WidgetMetricTile
                      metricProperties={{
                        type: WidgetMetricType.RegularMetric,
                        properties: {
                          metricTitle: t(
                            kpiTargetTranslations.kpiProperties.target.title.key,
                            kpiTargetTranslations.kpiProperties.target.title.defaultValue,
                          ),
                          metricValue: `${target.toFixed(2)}%`,
                        },
                      }}
                    />
                  </RowFlexContainer>
                )} */}
                <ColumnFlexContainer flex={1} gap={[2]}>
                  <MetricItem
                    icon={Icons.CONVERSION}
                    indicatorColor="primary"
                    titleText={t(
                      rfidSalesRetaggingTranslations.addedSalesTitle.key,
                      rfidSalesRetaggingTranslations.addedSalesTitle.defaultValue,
                    )}
                    descriptionText={t(
                      rfidSalesRetaggingTranslations.addedSalesDescription.key,
                      rfidSalesRetaggingTranslations.addedSalesDescription.defaultValue,
                    )}
                    percentValue={rfidSalesRetagging.addedSales}
                  />

                  <MetricItem
                    icon={Icons.TAG}
                    indicatorColor="primary"
                    titleText={t(
                      rfidSalesRetaggingTranslations.totalSuccessfulRetaggingTitle.key,
                      rfidSalesRetaggingTranslations.totalSuccessfulRetaggingTitle.defaultValue,
                    )}
                    descriptionText={t(
                      rfidSalesRetaggingTranslations.totalSuccessfulRetaggingDescription.key,
                      rfidSalesRetaggingTranslations.totalSuccessfulRetaggingDescription.defaultValue,
                    )}
                    rawValue={rfidSalesRetagging.totalSuccessfulRetagging}
                    unit="pcs"
                  />
                </ColumnFlexContainer>
              </ColumnFlexContainer>
            )}
          </ColumnFlexContainer>
        }
      />
    );
  },
);
